import styled from 'styled-components';

import { useBreakpoint } from '@contexts/breakpoints';
import Skeleton from '@components/skeletons/Skeleton';
import { Col, FlexibleDiv, Row, Separator } from '@components/formStyles';

export const SkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  overflow: hidden;
  max-width: 1200px;
`;

export const SkeletonContentWrapper = styled.div`
  width: 1284px;
  margin: 0 auto;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    padding: 0 12px;
    width: 100%;
    max-width: 100%;
  }
`;

export const ImagesSkeleton = styled(Row)`
  width: 100%;
  cursor: default;
  justify-content: flex-start;
  margin: 24px auto 32px;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X_S})`} {
    padding: 0;
    margin: 16px 0 24px;
  }
`;

export const MainImageWrapperSkeleton = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  max-width: 820px;
`;

export const SideColSkeleton = styled(Col)`
  margin-left: 12px;
  row-gap: 12px;
`;

export const SummarySkeleton = styled.div`
  width: 100%;
  background: transparent;
  box-shadow: none;
  position: relative;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    padding: 0 0 24px 0;
    margin-bottom: 16px;
  }
`;

export const OpportunitySkeleton = styled.div`
  padding: 32px 0 24px;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    padding: 24px 0 16px;
  }
`;

export const OpportunitySkeletonContent = styled.div`
  padding: 20px 28px;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    padding: 16px 16px 18px;
  }
`;

export const AmenitySkeleton = styled.div`
  padding: 8px 0 48px;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    padding: 0 0 32px;
  }
`;

export const AmenitySkeletonContent = styled.div`
  max-width: 900px;
`;

const MultipleImageViewSkeleton = () => {
  const { xl } = useBreakpoint();

  return (
    <ImagesSkeleton>
      <MainImageWrapperSkeleton>
        <Skeleton
          width={xl ? 592 : undefined}
          height={xl ? 382 : 206}
          borderRadius={xl ? 8 : 0}
        />
      </MainImageWrapperSkeleton>

      {xl ? (
        <>
          <SideColSkeleton>
            <FlexibleDiv bottom={12}>
              <Skeleton width={288} height={183} borderRadius={8} />
            </FlexibleDiv>
            <FlexibleDiv>
              <Skeleton width={288} height={183} borderRadius={8} />
            </FlexibleDiv>
          </SideColSkeleton>
          <SideColSkeleton>
            <FlexibleDiv bottom={12}>
              <Skeleton width={288} height={183} borderRadius={8} />
            </FlexibleDiv>
            <FlexibleDiv>
              <Skeleton width={288} height={183} borderRadius={8} />
            </FlexibleDiv>
          </SideColSkeleton>
        </>
      ) : null}
    </ImagesSkeleton>
  );
};

const DetailSummarySkeleton = () => {
  const { xl, md, xs } = useBreakpoint();

  const textWidth1 = xl ? 720 : md ? 520 : xs ? 300 : 600;
  const textWidth2 = xl ? 680 : md ? 480 : xs ? 280 : 560;
  const textWidth3 = xl ? 640 : md ? 450 : xs ? 260 : 520;

  return (
    <SummarySkeleton>
      <Row alignItems='flex-start' justifyContent='space-between'>
        <Col>
          {/* Title */}
          <FlexibleDiv bottom={10}>
            <Skeleton
              width={md ? 260 : 320}
              height={md ? 32 : 40}
              borderRadius={4}
            />
          </FlexibleDiv>

          <FlexibleDiv bottom={16}>
            <Row alignItems='center'>
              <FlexibleDiv right={8}>
                <Skeleton width={16} height={16} borderRadius={4} />
              </FlexibleDiv>
              <FlexibleDiv right={8}>
                <Skeleton width={160} height={16} borderRadius={4} />
              </FlexibleDiv>
              <FlexibleDiv>
                <Skeleton width={110} height={16} borderRadius={4} />
              </FlexibleDiv>
            </Row>
          </FlexibleDiv>

          <FlexibleDiv bottom={6}>
            <Skeleton width={textWidth1} height={16} borderRadius={4} />
          </FlexibleDiv>
          <FlexibleDiv bottom={6}>
            <Skeleton width={textWidth2} height={16} borderRadius={4} />
          </FlexibleDiv>
          <FlexibleDiv bottom={10}>
            <Skeleton width={textWidth3} height={16} borderRadius={4} />
          </FlexibleDiv>
        </Col>
      </Row>
    </SummarySkeleton>
  );
};

const TabMenuSkeleton = () => {
  const tabs = [1, 2, 3, 4, 5, 6];

  return (
    <FlexibleDiv bottom={24} top={8}>
      <Row alignItems='flex-end'>
        {tabs.map((i) => (
          <FlexibleDiv key={i} right={20}>
            <Row alignItems='center' style={{ height: 40, padding: '0 4px' }}>
              <Skeleton width={70} height={18} borderRadius={4} />
            </Row>
          </FlexibleDiv>
        ))}
      </Row>

      {/* Alt çizgi + aktif tab için kalın çizgi */}
      <FlexibleDiv top={8}>
        <Skeleton width={900} height={2} borderRadius={2} />
      </FlexibleDiv>
    </FlexibleDiv>
  );
};

/* OTEL ÖZELLİKLERİ – accordion kartı */

const DetailOpportunitySkeleton = () => {
  const { xl } = useBreakpoint();

  return (
    <OpportunitySkeleton>
      {/* Section title “Otel Özellikleri” */}
      <FlexibleDiv bottom={20}>
        <Skeleton width={180} height={24} borderRadius={4} />
      </FlexibleDiv>

      <OpportunitySkeletonContent>
        {/* Yaz Sezonu başlığı */}
        <FlexibleDiv
          bottom={24}
          style={{ textAlign: 'center', justifyContent: 'center' }}
        >
          <Skeleton width={140} height={22} borderRadius={999} />
        </FlexibleDiv>

        {/* Accordion satırları */}
        {[1, 2, 3, 4, 5].map((item) => (
          <FlexibleDiv key={item} bottom={item === 5 ? 0 : 16}>
            <Row alignItems='center' justifyContent='space-between'>
              <Skeleton width={xl ? 200 : 170} height={18} borderRadius={4} />
              <Skeleton width={22} height={22} borderRadius={4} />
            </Row>
          </FlexibleDiv>
        ))}
      </OpportunitySkeletonContent>
    </OpportunitySkeleton>
  );
};

/* GENEL ŞARTLAR – uzun metin bloğu */

const DetailAmenitySkeleton = () => {
  const { xl, md, xs } = useBreakpoint();

  const lineWidth = xl ? 820 : md ? 620 : xs ? 300 : 520;

  return (
    <AmenitySkeleton>
      {/* Section title “Genel Şartlar” */}
      <FlexibleDiv bottom={16}>
        <Skeleton width={160} height={24} borderRadius={4} />
      </FlexibleDiv>

      <AmenitySkeletonContent>
        {[1, 2, 3, 4, 5, 6].map((item) => (
          <FlexibleDiv key={item} bottom={8}>
            <Skeleton width={lineWidth} height={14} borderRadius={4} />
          </FlexibleDiv>
        ))}
      </AmenitySkeletonContent>
    </AmenitySkeleton>
  );
};

const HotelDetailSkeleton = () => {
  return (
    <SkeletonWrapper>
      <MultipleImageViewSkeleton />
      <SkeletonContentWrapper>
        <DetailSummarySkeleton />
        <TabMenuSkeleton />
        <Separator size='32px' />
        <DetailOpportunitySkeleton />
        <Separator size='32px' />
        <DetailAmenitySkeleton />
      </SkeletonContentWrapper>
    </SkeletonWrapper>
  );
};

export default HotelDetailSkeleton;
